import {
  FirestoreCollectionProsessorOptions,
  createFirestoreCollectionProsessor,
} from "../firebase/firestore";

import "./ViewDependSheetField";
import "./ViewDependSheet";
import "./ViewConfigSchema";

export type View = {
  id: string;
  viewType: string;
  title: string;
  description: string;
  alias: string | null;
  createdAt: string;
  updatedAt: string;
  isDisabled: boolean;
  sort: number;
};
type ViewRaw = {
  id: string;
  viewType: string;
  title: string;
  description: string;
  // NOTE: alias導入前の既存ドキュメントにはフィールドが存在しないためoptional
  alias?: string | null;
  created_at: string;
  updated_at: string;
  is_disabled: boolean;
  sort: number;
};

export const checkViewHasBlankId = (v: View) => {
  return v.id === "";
};

/**
 * titleからビューグループとビュー名をパースする => groupName/viewName
 */
export const parseViewTitle = (
  title: string
): {
  groupName: string | null;
  viewName: string;
  error: string | null;
} => {
  const slashCount = (title.match(/\//g) || []).length;

  // スラッシュが2個以上の場合はエラー
  if (slashCount >= 2) {
    return {
      groupName: null,
      viewName: title,
      error: `/（スラッシュ）は1つまで使用できます`,
    };
  }

  // スラッシュがない場合
  if (slashCount === 0) {
    return {
      groupName: null,
      viewName: title,
      error: null,
    };
  }

  // スラッシュが1個の場合
  const parts = title.split("/");
  const groupName = parts[0].trim();
  const viewName = parts[1].trim();

  return {
    groupName: groupName || null,
    viewName: viewName || title,
    error: null,
  };
};

/**
 * URLセーフなalias（RFC 3986 非予約文字: 英大文字・小文字・数字・ハイフン・ピリオド・アンダースコア・チルダ）のみ許可する
 */
const VIEW_ALIAS_PATTERN = /^[A-Za-z0-9._~-]+$/;

/**
 * Viewのaliasの妥当性を検証する。問題なければnull、エラーがあればエラーメッセージを返す。
 * - 空欄は許可（alias任意）
 * - URLセーフな文字（RFC 3986 非予約文字: 英大文字・英小文字・数字・ハイフン・ピリオド・アンダースコア・チルダ）のみ許可。スラッシュ等は不可
 * - 単独の . / .. はURLのdot-segment正規化で到達不能になるため不可
 * - 他のViewのaliasと重複不可
 * - 他のViewのドキュメントIDと衝突不可（alias→ID解決の一意性を担保）
 * @param alias 検証対象のalias
 * @param views 同一バージョン内の全View
 * @param selfId 編集中のView自身のID（重複判定から除外する）
 */
export const validateViewAlias = (
  alias: string | null | undefined,
  views: View[],
  selfId: string
): string | null => {
  // 未設定・空欄は許可（alias任意）
  if (!alias) {
    return null;
  }
  if (!VIEW_ALIAS_PATTERN.test(alias)) {
    return "aliasは英大文字・英小文字・数字・ハイフン・ピリオド・アンダースコア・チルダのみ使用できます";
  }
  // dot-segment（. / ..）はURL正規化で消える/親パスへ解決されるため、alias URLで到達できなくなる。単独の . / .. は禁止する。
  if (alias === "." || alias === "..") {
    return "aliasを「.」または「..」のみにすることはできません";
  }
  const otherViews = views.filter((v) => v.id !== selfId);
  if (otherViews.some((v) => v.alias === alias)) {
    return "aliasが他のビューと重複しています";
  }
  // 自身のIDも含め、既存ビューIDとの衝突を禁止する（alias=ID時のリダイレクトループ防止）
  if (views.some((v) => v.id === alias)) {
    return "aliasに既存のビューIDは使用できません";
  }
  return null;
};

const processorOptions: FirestoreCollectionProsessorOptions<
  View,
  ViewRaw,
  Record<string, string>
> = {
  getCollectionPath: ({ organizationId, versionId }) =>
    `/app/spreadsheet_ui/v/1/organizations/${organizationId}/versions/${versionId}/views`,
  toFirestore: ({
    id,
    title,
    viewType,
    description,
    alias,
    createdAt,
    updatedAt,
    isDisabled,
    sort,
  }) => ({
    id,
    title,
    viewType,
    description,
    // NOTE: aliasなしはnullに正規化する（Firestoreはundefined不可。空文字もnull扱い）
    alias: alias || null,
    created_at: createdAt,
    updated_at: updatedAt,
    is_disabled: isDisabled,
    sort,
  }),
  fromFirestore: ({
    id,
    title,
    viewType,
    description,
    alias,
    created_at,
    updated_at,
    is_disabled,
    sort,
  }) => ({
    id,
    title,
    viewType,
    description,
    // NOTE: alias未設定の既存ドキュメント（undefined）はnullに正規化する
    alias: alias ?? null,
    createdAt: created_at,
    updatedAt: updated_at,
    isDisabled: is_disabled,
    sort,
  }),
};

export const {
  useGetAll: useGetViews,
  useGet: useGetView,
  useSet: useSetView,
  useUpdate: useUpdateView,
  useRecursiveDelete: useRecursiveDeleteView,
  useItem: useView,
  useItems: useViews,
} = createFirestoreCollectionProsessor(processorOptions);
